import NextImage from 'next/image';

import {
    Box,
    Heading,
    Image,
    Text,
    useBreakpointValue,
    useChakraContext,
    useSlotRecipe,
    VStack,
} from '@chakra-ui/react';

import { urlJoin } from 'url-join-ts';

import { GridWrapper } from '@nocowanie/common-ui/components/atoms/grid-wrapper';

import { SectionNameEnum } from '@app/enums';
import { environment } from '@app/environments/environment';
import { HeroContent } from '@app/models/cms-api';
import * as cmsHomepageSlice from '@app/state/slices/cms-homepage';
import { useAppSelector } from '@app/state/state';

export const Hero = () => {
    const recipe = useSlotRecipe({ key: 'hero' });
    const styles = recipe();

    const heroData: HeroContent = useAppSelector(
        cmsHomepageSlice.selectors.selectSectionData(SectionNameEnum.HeroSection),
    );

    const imgSrc = urlJoin(environment.ASSETS_PATH, 'svg', 'hero-stars.svg');

    const theme = useChakraContext();
    const sm = theme.breakpoints.values.find(b => b.name === 'sm')?.min;
    const lg = theme.breakpoints.values.find(b => b.name === 'lg')?.min;
    const withDecorator = useBreakpointValue({
        base: false,
        lg: true,
    });

    if (!heroData) return;

    const { header, headerMobile, subheader, imageLg, imageMd, imageSm } = heroData;

    return (
        <Box data-testid={'hero-wrapper'} css={styles.wrapper}>
            <picture>
                <source media={`(min-width: ${lg})`} srcSet={imageLg?.url} />
                <source media={`(min-width: ${sm})`} srcSet={imageMd?.url} />
                <Image
                    src={imageSm?.url}
                    htmlWidth={480}
                    htmlHeight={510}
                    alt={''}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        mixBlendMode: 'darken',
                    }}
                    loading={'eager'}
                    {...({ fetchpriority: 'high' } as React.HTMLAttributes<HTMLImageElement>)}
                />
            </picture>

            <GridWrapper
                gridTemplateColumns={{ base: '1fr' }}
                position={'absolute'}
                top={'50%'}
                left={'50%'}
                transform={'translate(-50%, -50%)'}
            >
                <VStack
                    justifyContent="center"
                    alignItems="start"
                    gap={7}
                    my={{ base: 16, lg: 24, xl: 40 }}
                >
                    <Heading css={styles.heading} as="h1" size={'3xl'}>
                        <Text as={'span'} hideBelow={'sm'}>
                            {header}
                        </Text>
                        <Text as={'span'} hideFrom={'sm'}>
                            {headerMobile ?? header}
                        </Text>
                        {withDecorator ? (
                            <Image
                                position={'relative'}
                                objectFit={'contain'}
                                marginTop={-12}
                                alt=""
                                asChild
                            >
                                <NextImage alt="" src={imgSrc} width={55} height={65} />
                            </Image>
                        ) : null}
                    </Heading>
                    <Text
                        hideBelow={'sm'}
                        color="white"
                        fontSize={{ base: 'lg', md: '2xl' }}
                        maxW="450px"
                    >
                        {subheader}
                    </Text>
                </VStack>
            </GridWrapper>
        </Box>
    );
};
